import { featuredDeep, relatedDeep } from "@/mocks/chigua";
import type { ArticleLike } from "./ArticleModal";

interface DeepReviewProps {
  onRead: (article: ArticleLike) => void;
}

export default function DeepReview({ onRead }: DeepReviewProps) {
  const deepArticle: ArticleLike = {
    category: "深度复盘",
    deep: true,
    title: featuredDeep.title,
    meta: `${featuredDeep.author} · ${featuredDeep.date} · 阅读约 ${featuredDeep.readMinutes} 分钟`,
    image: featuredDeep.image,
    alt: featuredDeep.alt,
    paragraphs: featuredDeep.blocks.map((b) => (b.heading ? `${b.heading}。${b.paragraph ?? ""}` : b.paragraph ?? "")),
  };

  const openRelated = (title: string, time: string, blurb: string) => {
    onRead({
      category: "相关阅读",
      title,
      meta: time,
      paragraphs: [blurb, "本文为看吃瓜编辑部原创深度内容，欢迎收藏阅读，也欢迎留言聊聊你的观点。"],
    });
  };

  return (
    <section id="deep" className="scroll-mt-20 overflow-hidden bg-background-100 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-sm font-semibold tracking-widest text-primary-500 uppercase">深度复盘</p>
            <h2 className="mt-2 font-fun text-3xl text-foreground-950 md:text-4xl">不止吃瓜，更把瓜吃透</h2>
            <p className="mt-2 max-w-xl text-foreground-600">
              单条热搜太碎片？我们为热议事件梳理完整时间线，帮你把来龙去脉一次补齐。
            </p>
          </div>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-[1fr_360px]">
          <article className="group overflow-hidden rounded-3xl border border-background-200 bg-background-50">
            <button
              type="button"
              onClick={() => onRead(deepArticle)}
              className="relative block w-full cursor-pointer overflow-hidden"
              aria-label={`阅读深度复盘：${featuredDeep.title}`}
            >
              <img
                src={featuredDeep.image}
                alt={featuredDeep.alt}
                className="aspect-[16/9] w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent"></div>
              <span className="absolute left-5 top-5 inline-flex items-center gap-1 rounded-full bg-accent-400 px-3 py-1 text-xs font-bold text-primary-950">
                <i className="ri-scan-2-line" aria-hidden="true"></i>编辑精选
              </span>
              <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
                <h3 className="max-w-2xl text-left text-xl font-bold leading-snug text-background-50 md:text-2xl">
                  {featuredDeep.title}
                </h3>
                <p className="mt-3 hidden max-w-2xl text-left text-sm text-background-50/85 md:line-clamp-2 md:block">
                  {featuredDeep.dek}
                </p>
              </div>
            </button>
            <div className="flex flex-wrap items-center justify-between gap-4 p-6">
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="inline-flex items-center gap-1.5 font-medium text-foreground-700">
                  <i className="ri-edit-2-line text-primary-500" aria-hidden="true"></i>
                  {featuredDeep.author}
                </span>
                <span>
                  <i className="ri-time-line mr-1" aria-hidden="true"></i>
                  {featuredDeep.readMinutes} 分钟读完
                </span>
              </div>
              <button
                type="button"
                onClick={() => onRead(deepArticle)}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
              >
                展开完整复盘 <i className="ri-arrow-right-up-line" aria-hidden="true"></i>
              </button>
            </div>
          </article>

          <aside className="space-y-6">
            <div className="overflow-hidden rounded-2xl border border-background-200 bg-background-50">
              <div className="flex items-center gap-2 border-b border-background-200 px-5 py-4">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                  <i className="ri-stack-line" aria-hidden="true"></i>
                </span>
                <h3 className="font-fun text-lg text-foreground-900">编辑部相关推荐</h3>
              </div>
              <ul className="divide-y divide-background-200">
                {relatedDeep.map((r, i) => (
                  <li key={r.title}>
                    <button
                      type="button"
                      onClick={() => openRelated(r.title, r.time, r.blurb)}
                      className="group flex w-full items-start gap-3 px-5 py-4 text-left transition-colors hover:bg-primary-50/50"
                    >
                      <span className="mt-1 font-fun text-primary-400">{String(i + 1).padStart(2, "0")}</span>
                      <span className="flex-1">
                        <span className="line-clamp-2 font-medium text-foreground-900 transition-colors group-hover:text-primary-700">
                          {r.title}
                        </span>
                        <span className="mt-1 block text-xs text-foreground-400">
                          <i className="ri-eye-line mr-1" aria-hidden="true"></i>
                          {r.time}
                        </span>
                      </span>
                      <i className="ri-arrow-right-line mt-1 text-foreground-300 transition-all group-hover:translate-x-1 group-hover:text-primary-500" aria-hidden="true"></i>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl bg-secondary-100 p-6">
              <h3 className="flex items-center gap-2 font-fun text-lg text-secondary-900">
                <i className="ri-shield-check-line" aria-hidden="true"></i>围观原则
              </h3>
              <p className="mt-2 text-sm leading-6 text-secondary-900/80">
                先求证再发布、明示待证信息、及时跟进反转。我们想让吃瓜更有质量，也让围观少一点误伤。
              </p>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}